<script setup>
import { TextInput, BrandButton, NoticeBar, NoticeBarTypes } from '@thunderbirdops/services-ui';
import { computed, ref, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import { PhArrowRight } from '@phosphor-icons/vue';

const route = useRoute();

const errors = window._page.currentView?.errors;
const formAction = window._page.currentView?.formAction;
const tbProPrimaryDomain = `@${window._page.currentView?.tbProPrimaryDomain}`;
const attributeValues = window._page.currentView?.attributes;
const message = window._page?.message;

// Fixed values
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
const locale = window._page.currentView?.currentLocale;

// Computed values
const username = ref(attributeValues?.username?.split('@')[0] || null);
const email = computed(() => {
  return `${username.value}@thundermail.com`;
});
const recoveryEmail = ref(route.query?.email || attributeValues?.email || null);

const usernameError = computed(() => {
  return errors?.username === '' ? null : errors?.username;
});
const passwordError = computed(() => {
  return errors?.password === '' ? null : errors?.password;
});
const passwordConfirmError = computed(() => {
  return errors?.passwordConfirm === '' ? null : errors?.passwordConfirm;
});
const recoveryEmailError = computed(() => {
  return errors?.email === '' ? null : errors?.email;
});

const registerForm = useTemplateRef('register-form');
// @submit and @keyup.enter can both fire for one Enter keypress; keep the POST single-shot (#1231).
const isSubmitting = ref(false);

const onSubmit = () => {
  if (isSubmitting.value) return;
  // Do some basic password/confirm password checking, due to how we inject the i18n between kc and accounts 
  // we'll need to just grab it from the html. 
  const invalidPasswordConfirmMessage = document.getElementById('invalidPasswordConfirmMessage')?.innerText;
  const password = registerForm.value.elements['password'];
  const passwordConfirm = registerForm.value.elements['password-confirm'];

  if (invalidPasswordConfirmMessage
    && password.value
    && passwordConfirm.value
    && password.value !== passwordConfirm.value) {
    password.setCustomValidity(invalidPasswordConfirmMessage);
    passwordConfirm.setCustomValidity(invalidPasswordConfirmMessage);
  }

  if (registerForm.value.checkValidity()) {
    isSubmitting.value = true;
    registerForm.value.submit();
  }
};

</script>

<script>
export default { 
  name: 'RegisterView'
};
</script>

<template>
  <div id="i18n-workaround">
    <span id="invalidPasswordConfirmMessage">{{ $t('invalidPasswordConfirmMessage') }}</span>
  </div>

  <h2 data-testid="title">{{ $t('registerTitle') }}</h2>

  <notice-bar
    data-testid="error-notice-bar"
    :type="NoticeBarTypes.Critical"
    v-if="!message?.type && (usernameError || passwordError || passwordConfirmError || recoveryEmailError)"
  >
    {{ $t('registerError') }}
  </notice-bar>

  <form id="kc-register-form" ref="register-form" method="POST" :action="formAction" @submit.prevent="onSubmit"
        @keyup.enter="onSubmit">
    <div class="form-elements">
      <text-input
        data-testid="username-input"
        id="partialUsername"
        name="partialUsername"
        required
        autocomplete="username" 
        :error="usernameError"
        :outerSuffix="tbProPrimaryDomain"
        :help="$t('signUpUsernameHelp', {'domain': tbProPrimaryDomain})"
        v-model="username"
      >
        {{ $t('username') }}
      </text-input>
      <text-input
        data-testid="recovery-email-input"
        id="email"
        name="email"
        required
        autocomplete="email"
        type="email"
        :error="recoveryEmailError"
        :help="$t('recoveryEmailHelp')"
        v-model="recoveryEmail"
      >
        {{ $t('recoveryEmail') }}
      </text-input>
      <text-input
        data-testid="password-input"
        id="password"
        name="password"
        required
        autocomplete="new-password"
        type="password"
        :error="passwordError"
        :help="$t('signUpPasswordHelp')"
      >
        {{ $t('password') }}
      </text-input>
      <text-input
        data-testid="password-confirm-input"
        id="password-confirm"
        name="password-confirm"
        required
        autocomplete="confirm-password"
        type="password"
        :error="passwordConfirmError"
        :help="$t('signUpPasswordConfirmHelp')"
      >
        {{ $t('passwordConfirm') }}
      </text-input>
      <!-- These fields are dynamically filled out -->
      <text-input readonly data-testid="full-username-readonly-input" id="username" name="username" class="hidden" v-model="email"></text-input>
      <text-input readonly data-testid="locale-readonly-input" id="locale" name="locale" class="hidden" v-model="locale"></text-input>
      <text-input readonly data-testid="zoneinfo-readonly-input" id="zoneinfo" name="zoneinfo" class="hidden" v-model="timezone"></text-input>
      <slot name="form-extras"/>
    </div>
    <div class="buttons">
      <brand-button data-testid="submit-button" class="submit" @click="onSubmit" :disabled="isSubmitting">
        {{ $t('doRegister') }}

        <template #iconRight>
          <ph-arrow-right size="20" />
        </template>
      </brand-button>
    </div>
  </form>
</template>

<style scoped>
#i18n-workaround {
  display: none;
}

.hidden {
  display: none;
}

.form-elements {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.buttons {
  margin-top: var(--space-24);
  width: 100%;

  .submit {
    margin-right: 0;
    margin-left: auto;
  }
}
</style>
